/**
 * Shell chrome helpers shared by the header bar and its controls
 * (White signal v2; Paper → App → "App v3 · … · LP-aligned").
 */

/** Header control: 34px, 8px radius, hairline ring, 12px/500 ink, 13px muted glyph. */
export const shellControlClass =
  "inline-flex h-[34px] items-center gap-1.5 rounded-lg px-2.5 text-[12px] font-medium leading-4 text-foreground/90 " +
  "shadow-[0_0_0_1px_rgb(0_0_0/0.08)] dark:shadow-[0_0_0_1px_rgb(255_255_255/0.1)] bg-transparent " +
  "transition-colors hover:bg-black/[0.03] dark:hover:bg-white/5 data-[state=open]:bg-black/[0.03] " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 [&_svg]:text-foreground/45"

export interface ShellCrumb {
  group?: string
  page: string
  /** Set when the route sits below the page (e.g. a visitor profile): the page crumb links back here. */
  href?: string
}

const ANALYTICS_SEGMENTS = [
  "traffic",
  "pages",
  "devices",
  "locations",
  "channels",
  "llm-referrals",
  "clicks",
  "forms",
  "bots",
  "visitors",
]

const TOOL_PAGES: Record<string, string> = {
  "utm-generator": "UTM generator",
  "significance-calculator": "Significance calculator",
  "conversion-rate-calculator": "Conversion rate calculator",
}

const CONNECTOR_PAGES: Record<string, string> = {
  stripe: "Stripe",
  foxy: "Foxy",
  jotform: "Jotform",
  typeform: "Typeform",
  tally: "Tally",
  webflow: "Webflow",
}

function toolCrumb(sub: string | undefined): ShellCrumb {
  const page = sub && TOOL_PAGES[sub]
  return page ? { group: "Marketing tools", page } : { page: "Marketing tools" }
}

/** Map a pathname onto the sidebar IA ("Group › Page"). Null for routes with no crumb. */
export function resolveShellCrumb(pathname: string): ShellCrumb | null {
  if (pathname === "/dashboard" || pathname === "/dashboard/") return { page: "Home" }
  if (pathname.startsWith("/user-settings")) return { page: "Account settings" }
  if (pathname.startsWith("/tools")) return toolCrumb(pathname.split("/")[2])

  // Strip the scope prefix (/workspace/:id or /p/:id) so the rest reads the same.
  const scoped = pathname.match(/^\/(workspace|p)\/[^/]+(\/.*)?$/)
  const scope = scoped?.[1]
  const rest = (scoped ? scoped[2] : pathname) || "/"
  const segs = rest.split("/").filter(Boolean)
  const seg = segs[0] ?? ""

  if (seg === "visitors") {
    const base = scoped ? pathname.slice(0, pathname.length - (scoped[2] ?? "").length) : ""
    return { group: "Observe", page: "Analytics", href: `${base}/traffic` }
  }
  if (ANALYTICS_SEGMENTS.includes(seg)) return { group: "Observe", page: "Analytics" }
  if (seg.startsWith("heatmap")) return { group: "Observe", page: "Heatmaps" }
  if (seg === "funnels" || seg === "funnel-analyzer") return { group: "Observe", page: "Funnels" }
  if (seg === "attribution") return { group: "Observe", page: "Attribution" }
  if (seg === "test-planner") return { group: "Experiments", page: "A/B test planner" }
  if (["split-testing", "split-tests", "experiments"].includes(seg))
    return { group: "Optimize", page: "Experiments" }
  if (seg === "api") return { group: "Agent & data", page: "API & MCP" }
  if (seg === "connectors")
    return segs[1] && CONNECTOR_PAGES[segs[1]]
      ? { group: "Connectors", page: CONNECTOR_PAGES[segs[1]] }
      : { group: "Agent & data", page: "Connectors" }
  if (seg === "export") return { group: "Agent & data", page: "Export" }
  if (seg === "tools") return toolCrumb(segs[1])
  if (seg === "resources") return { page: "Resources & support" }
  if (seg === "settings")
    return scope === "p"
      ? { group: "Workspace settings", page: "Site settings" }
      : { group: "Account settings", page: "Workspace settings" }
  return null
}

const ANALYTICS_TAB_SEGMENTS = ANALYTICS_SEGMENTS.filter((seg) => seg !== "visitors")

/** True when the route is one of the Analytics tab pages (the shell renders the tab bar). */
export function isAnalyticsTabPath(pathname: string): boolean {
  const scoped = pathname.match(/^\/(workspace|p)\/[^/]+\/([^/]+)/)
  return !!scoped && ANALYTICS_TAB_SEGMENTS.includes(scoped[2])
}
